import React from 'react';
import classNames from 'classnames';
import {Link} from '../Link.jsx';
import {Picture} from '../Picture.jsx';
import {TextPosition} from '../text-modifiers';
import {Headline} from '../Headline.jsx';
import {Subline} from '../Subline.jsx';
import {CallToAction} from '../CallToAction.jsx';
import {PriceTag} from '../PriceTag.jsx';
import {Video} from '../Video.jsx';
import {TrackingContextAppend} from '../TrackingContext.jsx';
import {CfEntry} from '../CfPreviewContext.jsx';
import {LinkBarV2} from './LinkBarV2.jsx';
import {XRay} from '@teaser/scripts/components/XRay.jsx';

/**
 * @typedef {TeaserLinkModel} HalfSizeTeaserModel
 * @property {'HALF_SIZE_TEASER'} type
 * @property {string | null} headline
 * @property {string | null} subline
 * @property {string} backgroundColor
 * @property {ImageModel | null} backgroundImage
 * @property {VideoModel | null} video
 * @property {string | null} callToAction
 * @property {TextPosition} textPosition
 * @property {TextColor} textColor
 * @property {PriceTagModel} priceTags
 * @property {LinkBarV2} linkBar
 * @property {string} contentType
 * @property {boolean} isPortrait
 * @property {string} entryId
 * @property {Device[]} devices
 */

/**
 * @typedef {import('./ArticleOverviewTeaserContent.jsx').Device} Device
 */

/**
 * @param {HalfSizeTeaserModel} model
 * @param {Device} device
 * @returns {JSX.Element}
 */
export function HalfSizeTeaser({model, device}) {
    return (
        <TrackingContextAppend value={{id: model.entryId}}>
            <CfEntry entryId={model.entryId} contentType={model.contentType}>
                <article
                    className={classNames(
                        'content-platform__teaser',
                        'content-platform__teaser--half-size'
                    )}
                >
                    <XRay fieldId="internalName" />
                    <div
                        className="content-platform__teaser__container content-platform__teaser--half-size__container"
                        style={{backgroundColor: model.backgroundColor}}
                    >
                        <Link model={model}>
                            <Video model={model} aspectRatio={model.isPortrait ? 0.714 : 1}>
                                {model.backgroundImage ? (
                                    <Picture
                                        model={model.backgroundImage}
                                        xxlarge={920}
                                        xlarge={780}
                                        large={574}
                                        medium={767}
                                        small={479}
                                        aspectRatio={model.isPortrait ? 0.714 : 1}
                                    />
                                ) : (
                                    <div className="content-platform__half-size-teaser__spacer">&nbsp;</div>
                                )}
                            </Video>

                            <div
                                className={classNames(
                                    'content-platform__teaser__content',
                                    model.subline && '-with-subline',
                                )}
                            >
                                <div
                                    className={classNames(
                                        'content-platform__teaser__main',
                                        model.subline && '-with-subline',
                                        TextPosition[model.textPosition],
                                    )}
                                >
                                    <span className="content-platform__teaser__title_link">
                                        <Headline textColor={model.textColor} text={model.headline}/>
                                    </span>
                                    <span className="content-platform__teaser__subtitle__link">
                                        <Subline textColor={model.textColor} text={model.subline}/>
                                    </span>

                                    <CallToAction text={model.callToAction}/>

                                </div>
                            </div>
                        </Link>

                        {model.priceTags.map((priceTag) => (
                            <PriceTag key={priceTag.entryId} model={priceTag}/>
                        ))}
                    </div>

                    {model.linkBar && <LinkBarV2 model={model.linkBar}/>}
                </article>
            </CfEntry>
        </TrackingContextAppend>
    );
}
